import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ApiService } from '../services/api';
import { StoryService } from '../services/StoryService';
import { StoryResponse } from '../types/story';
import Modal from '../components/common/Modal';
import Notification, { NotificationType } from '../components/common/Notification';
import '../styles/pages/MyStories.css';
import { logger } from '../utils/logger';

/**
 * MyStories page component
 */
const MyStories: React.FC = () => {
  const [stories, setStories] = useState<StoryResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [storyToDelete, setStoryToDelete] = useState<string | null>(null);
  const [notification, setNotification] = useState({
    isOpen: false,
    type: 'success' as NotificationType,
    message: ''
  });
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();

  // Fetch stories when component mounts
  useEffect(() => {
    const fetchStories = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const userStories = await ApiService.getUserStories();
        setStories(userStories);
      } catch (err) {
        logger.error('Error fetching stories:', err);
        setError(err instanceof Error ? err.message : 'Failed to load your stories');
      } finally {
        setLoading(false);
      }
    };

    fetchStories();
  }, []);

  // Show notification
  const showNotification = (type: NotificationType, message: string) => {
    setNotification({
      isOpen: true,
      type,
      message
    });
  };

  // Close notification
  const closeNotification = () => {
    setNotification(prev => ({
      ...prev,
      isOpen: false
    }));
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Handle viewing a story
  const handleViewStory = (story: StoryResponse) => {
    // Store the story in session storage to access it on the view page
    sessionStorage.setItem('viewStory', JSON.stringify(story));
    navigate(`/story/${story.id}`);
  };

  // Handle sharing a story
  const handleShareStory = async (story: StoryResponse) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication required to share a story');
      }
      
      // Make the story public if it's not already
      if (!story.isPublic) {
        await ApiService.updateStoryVisibility(story.id!, true);
        // Update the story in the list
        setStories(prevStories => 
          prevStories.map(s => s.id === story.id ? { ...s, isPublic: true } : s)
        );
      }
      
      // Copy share link to clipboard
      const storyUrl = window.location.origin + `/shared/${story.shareCode}`;
      await navigator.clipboard.writeText(storyUrl);
      
      // Show success notification
      showNotification('success', 'Share link copied to clipboard!');
    } catch (err) {
      logger.error('Error sharing story:', err);
      alert(err instanceof Error ? err.message : 'Failed to share your story');
    }
  };

  // Open delete confirmation modal
  const confirmDeleteStory = (storyId: string) => {
    setStoryToDelete(storyId);
    setDeleteModalOpen(true);
  };

  // Handle deleting a story
  const handleDeleteStory = async () => {
    if (!storyToDelete) return;
    
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication required to delete a story');
      }
      
      await ApiService.deleteStory(storyToDelete);
      
      // Remove the story from the list
      setStories(prevStories => prevStories.filter(story => story.id !== storyToDelete));
      
      // Close the modal
      setDeleteModalOpen(false);
      setStoryToDelete(null);
      
      // Show success notification
      showNotification('success', 'Story deleted successfully');
    } catch (err) {
      logger.error('Error deleting story:', err);
      alert(err instanceof Error ? err.message : 'Failed to delete your story');
    }
  };

  // Handle toggling favorite status
  const handleToggleFavorite = async (story: StoryResponse, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering the card click
    
    try {
      const token = await getAccessToken();
      if (!token) return;
      
      // Get current favorite status from either property
      const currentFavoriteStatus = story.isFavorite || story.is_favorite || false;
      const newFavoriteStatus = !currentFavoriteStatus;
      
      // Call the service to update the favorite status
      await StoryService.toggleFavorite(story.id!, newFavoriteStatus, token);
      
      // Update the story in the list with the new favorite status
      setStories(prev => prev.map(s => 
        s.id === story.id ? {...s, isFavorite: newFavoriteStatus, is_favorite: newFavoriteStatus} : s
      ));
      
      // Show success notification
      showNotification(
        'success', 
        newFavoriteStatus ? 'Story added to favorites' : 'Story removed from favorites'
      );
    } catch (err) {
      logger.error('Error toggling favorite status:', err);
      showNotification('error', 'Failed to update favorite status');
    }
  };

  return (
    <div className="page-container">
      <h1 className="page-title">My Stories</h1>
      
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <div className="loading-message">Loading Your Stories</div>
        </div>
      )}
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      {!loading && !error && stories.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">📚</div>
          <h2>No stories yet</h2>
          <p>Create your first magical bedtime story to see it here!</p>
          <button 
            className="primary-button"
            onClick={() => navigate('/create')}
          >
            Create Story
          </button>
        </div>
      )}
      
      {!loading && !error && stories.length > 0 && (
        <div className="stories-grid">
          {stories.map(story => {
            // Handle both snake_case and camelCase property names
            const isFavorite = story.isFavorite || story.is_favorite || false;
            
            return (
              <div key={story.id} className="story-card">
                <div className="story-card-header">
                  <h3 
                    className="story-title clickable" 
                    onClick={() => handleViewStory(story)}
                  >
                    {story.title}
                  </h3>
                  <button 
                    className={`favorite-button ${isFavorite ? 'favorited' : ''}`}
                    onClick={(e) => handleToggleFavorite(story, e)}
                    aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
                    title={isFavorite ? "Remove from favorites" : "Add to favorites"}
                  >
                    {isFavorite ? "★" : "☆"}
                  </button>
                </div>
                {story.createdAt && (
                  <div className="story-date">Created on {formatDate(story.createdAt)}</div>
                )}
                <div className="story-preview">
                  {story.text.substring(0, 120)}... 
                </div>
                <div className="story-actions">
                  <button 
                    className="action-button secondary"
                    onClick={() => handleShareStory(story)}
                  >
                    Share
                  </button>
                  <button 
                    className="action-button danger"
                    onClick={() => confirmDeleteStory(story.id!)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setStoryToDelete(null);
        }}
        title="Delete Story"
        actions={
          <>
            <button 
              className="modal-button modal-button-secondary"
              onClick={() => {
                setDeleteModalOpen(false);
                setStoryToDelete(null);
              }}
            >
              Cancel
            </button>
            <button 
              className="modal-button modal-button-danger"
              onClick={handleDeleteStory}
            >
              Delete
            </button>
          </>
        }
      >
        <p>Are you sure you want to delete this story? This action cannot be undone.</p>
      </Modal>

      {/* Notification */}
      <Notification
        isOpen={notification.isOpen}
        type={notification.type}
        message={notification.message}
        onClose={closeNotification}
      />
    </div>
  );
};

export default MyStories; 